/**
 * Vista previa de 14 días de un patrón (008 · T087 · US5; contracts/ui.md §1.4).
 *
 * Muestra qué tocaría cada día si el patrón empezara en `startDay` con el desfase indicado. El
 * índice del ciclo replica `patternDayIndex` de la API (`modules/schedules/resolution.ts`, R-14):
 * semanal ⇒ lunes = 0 … domingo = 6 (alineado a la semana, sin desfase); ciclo libre ⇒ `(días
 * desde el inicio + desfase) mod N`. Es solo presentación: los días que de verdad se generan (y los
 * que se saltan por ajustes manuales o períodos cerrados) los decide el servidor (`dry_run`).
 */
import { localDayDiff, type SchedulePatternKind } from '@fermin/contracts';

import { t } from '../../../i18n';
import { cn } from '../../../lib/cn';
import {
  addDays,
  dayOfMonth,
  shiftsCode,
  shiftsDescription,
  weekdayLong,
  weekdayShort,
} from '../format';
import { isoWeekday } from '../holidays/holidays-format';

export const PREVIEW_DAYS = 14;

/** Datos de una plantilla que la vista previa necesita para pintar su código y horario. */
export interface PreviewTemplate {
  code: string;
  name: string;
  start_time: string;
  end_time: string;
}

/** Día del ciclo tal como está en el editor (`null` = aún sin definir). */
export interface PreviewPatternDay {
  kind: 'SHIFT' | 'REST' | null;
  template_ids: readonly string[];
}

/** Índice del ciclo para `day` (misma regla que la API). */
export function patternIndexFor(
  kind: SchedulePatternKind,
  cycleDays: number,
  startDay: string,
  cycleOffset: number,
  day: string,
): number {
  if (kind === 'WEEKLY') {
    return isoWeekday(day) - 1;
  }
  const raw = localDayDiff(startDay, day) + cycleOffset;
  return ((raw % cycleDays) + cycleDays) % cycleDays;
}

export interface PreviewCell {
  day: string;
  weekday: number;
  /** Índice del ciclo (0-based). */
  index: number;
  kind: 'SHIFT' | 'REST' | null;
  shifts: PreviewTemplate[];
}

/** Celdas de la vista previa. */
export function previewCells(
  kind: SchedulePatternKind,
  cycleDays: number,
  days: readonly PreviewPatternDay[],
  startDay: string,
  cycleOffset: number,
  templates: ReadonlyMap<string, PreviewTemplate>,
  count = PREVIEW_DAYS,
): PreviewCell[] {
  if (cycleDays < 1) {
    return [];
  }
  return Array.from({ length: count }, (_, offset) => {
    const day = addDays(startDay, offset);
    const index = patternIndexFor(kind, cycleDays, startDay, cycleOffset, day);
    const plan = days[index];
    const shifts =
      plan?.kind === 'SHIFT'
        ? plan.template_ids
            .map((id) => templates.get(id))
            .filter((template): template is PreviewTemplate => template !== undefined)
        : [];
    return { day, weekday: isoWeekday(day), index, kind: plan?.kind ?? null, shifts };
  });
}

/** Texto visible de la celda: `M`, `M+T`, `D` o `–`. */
export function previewCode(cell: PreviewCell): string {
  if (cell.kind === 'REST') {
    return t('schedules.grid.cell.restCode');
  }
  if (cell.kind === 'SHIFT' && cell.shifts.length > 0) {
    return shiftsCode(cell.shifts);
  }
  return '–';
}

/** "Martes 2 (día 3 del ciclo): Mañana 08:00–17:00". */
export function previewLabel(cell: PreviewCell): string {
  const content =
    cell.kind === 'REST'
      ? t('schedules.grid.cell.rest')
      : cell.kind === 'SHIFT' && cell.shifts.length > 0
        ? shiftsDescription(cell.shifts)
        : t('schedules.patterns.preview.undefined');
  return t('schedules.patterns.preview.cellLabel', {
    weekday: weekdayLong(cell.weekday),
    day: Number(dayOfMonth(cell.day)),
    index: cell.index + 1,
    content,
  });
}

export interface PatternPreviewProps {
  kind: SchedulePatternKind;
  cycleDays: number;
  days: readonly PreviewPatternDay[];
  startDay: string;
  cycleOffset?: number;
  templates: ReadonlyMap<string, PreviewTemplate>;
  className?: string;
}

export function PatternPreview({
  kind,
  cycleDays,
  days,
  startDay,
  cycleOffset = 0,
  templates,
  className,
}: PatternPreviewProps) {
  const cells = previewCells(kind, cycleDays, days, startDay, cycleOffset, templates);
  return (
    <ol
      data-testid="pattern-preview"
      aria-label={t('schedules.patterns.preview.label', { count: PREVIEW_DAYS })}
      className={cn('flex flex-wrap gap-fm-1', className)}
    >
      {cells.map((cell) => {
        const code = previewCode(cell);
        return (
          <li
            key={cell.day}
            data-testid="pattern-preview-day"
            data-day={cell.day}
            data-code={code}
            aria-label={previewLabel(cell)}
            title={previewLabel(cell)}
            className={cn(
              'flex h-fm-touch w-fm-touch flex-col items-center justify-center rounded-fm-sm border text-fm-xs',
              cell.kind === 'SHIFT'
                ? 'border-fm-border bg-fm-surface text-fm-text'
                : 'border-fm-border bg-fm-bg-sunken text-fm-text-secondary',
            )}
          >
            <span aria-hidden="true" className="text-[10px] leading-none text-fm-text-secondary">
              {weekdayShort(cell.weekday)} {dayOfMonth(cell.day)}
            </span>
            <span aria-hidden="true" className="font-semibold leading-tight">
              {code}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
